import { CloseOutlined } from '@ant-design/icons';
import { ConfigProvider, Image, Modal, Tag, Typography } from 'antd';
import { createStyles } from 'antd-style';
import { useMemo } from 'react';
import { EVENT_TYPE_MAP } from '@/services/event';
import { getResourceUrl } from '@/utils';

const { Paragraph } = Typography;

const useStyles = createStyles(() => ({
  modalContent: {
    overflow: 'hidden',
    borderRadius: 'var(--ant-border-radius-lg)',
  },
  modalHeader: {
    backgroundColor: '#475569',
    height: '56px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 48px 0 16px',
  },
  mediaContainer: {
    aspectRatio: '16 / 9',
    backgroundColor: 'black',
  },
  image: {
    objectFit: 'contain',
  },
}));

interface EventAttachmentModalProps {
  open: boolean;
  onClose: () => void;
  event?: API.EVENT.Event;
}

export default function EventAttachmentModal({
  open,
  onClose,
  event,
}: EventAttachmentModalProps) {
  const { styles } = useStyles();
  const type = useMemo(() => {
    if (!open) return '';

    if (event?.attachment) {
      const attachment = event.attachment;
      const ext = attachment.split('.').pop();
      if (!ext) return '';
      if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) {
        return 'image';
      } else if (['mp4'].includes(ext)) {
        return 'video';
      }
      return '';
    }
    return '';
  }, [open, event]);

  return (
    <ConfigProvider
      theme={{
        token: { paddingXL: 0 },
        components: { Typography: { titleMarginBottom: 0 } },
      }}
    >
      <Modal
        width="80vw"
        open={open}
        onCancel={onClose}
        closeIcon={<CloseOutlined style={{ color: 'white' }} />}
        destroyOnHidden
        centered
        footer={null}
        zIndex={3000}
      >
        {event ? (
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <Tag color={EVENT_TYPE_MAP[event.eventType].color}>
                {EVENT_TYPE_MAP[event.eventType].label}
              </Tag>
              <Paragraph
                style={{ color: 'white', marginBottom: 0 }}
                ellipsis={{ rows: 1, expandable: true, symbol: '更多' }}
              >
                {event.alertContent}
              </Paragraph>
            </div>
            <div className={styles.mediaContainer}>
              {type === 'image' && (
                <Image
                  className={styles.image}
                  width="100%"
                  height="100%"
                  src={getResourceUrl(event?.attachment)}
                  preview={false}
                />
              )}
              {type === 'video' && (
                <video
                  controls
                  width="100%"
                  height="100%"
                  src={getResourceUrl(event?.attachment)}
                >
                  <track kind="captions" />
                </video>
              )}
            </div>
          </div>
        ) : null}
      </Modal>
    </ConfigProvider>
  );
}
